import { useQuery } from '@tanstack/react-query'
import { api } from '@/services/api'
import { Card, StatCard, Badge } from '@/components/ui'
import { formatChartDate } from '@/utils/format-date'
import type { DetectionTrendPoint, AlertDeliveryStats } from '@spoofio/shared/types'

const STALE_TIME_MS = 60_000

export function AdminDashboardPage() {
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['admin-dashboard-stats'],
    queryFn: () => api.getAdminDashboardStats(),
    staleTime: STALE_TIME_MS,
  })

  const { data: trends } = useQuery({
    queryKey: ['admin-detection-trends'],
    queryFn: () => api.getDetectionTrends({ groupBy: 'day' }),
    staleTime: STALE_TIME_MS,
  })

  const { data: alertStats } = useQuery({
    queryKey: ['admin-alert-stats'],
    queryFn: () => api.getAlertDeliveryStats(),
    staleTime: STALE_TIME_MS,
  })

  if (isLoading) return <AdminDashboardSkeleton />

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-danger-600">Failed to load admin dashboard</p>
      </div>
    )
  }

  if (!stats) return null

  const byStatus = stats.organizations.byStatus as Partial<Record<string, number>>
  const activeTrials = byStatus.trial ?? 0
  const overduePayments = byStatus.past_due ?? 0

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Platform Admin Dashboard</h1>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Organizations"
          value={stats.organizations.total}
          icon={<OrgIcon />}
        />
        <StatCard
          title="Active Trials"
          value={activeTrials}
          icon={<TrialIcon />}
        />
        <StatCard
          title="Overdue Payments"
          value={overduePayments}
          changeType={overduePayments > 0 ? 'negative' : 'neutral'}
          icon={<PaymentIcon />}
        />
        <StatCard
          title="Detections (24h)"
          value={stats.detections.last24h}
          change={`${stats.detections.last7d} in 7d`}
          changeType="neutral"
          icon={<DetectionIcon />}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Detection Volume Trend">
          {trends && trends.length > 0 ? (
            <DetectionTrendChart data={trends} />
          ) : (
            <p className="text-gray-500 text-sm text-center py-6">No trend data available</p>
          )}
        </Card>

        <Card title="Alert Delivery Summary">
          {alertStats ? (
            <AlertDeliverySummary stats={alertStats} />
          ) : (
            <p className="text-gray-500 text-sm text-center py-6">No alert data available</p>
          )}
        </Card>
      </div>
    </div>
  )
}

function DetectionTrendChart({ data }: { data: DetectionTrendPoint[] }) {
  const maxCount = Math.max(...data.map((d) => d.count), 1)

  return (
    <div className="space-y-2">
      {data.map((point) => {
        const widthPercent = (point.count / maxCount) * 100
        const dateLabel = formatChartDate(point.date)
        return (
          <div key={point.date} className="flex items-center gap-3">
            <span className="text-xs text-gray-500 w-16 text-right flex-shrink-0">
              {dateLabel}
            </span>
            <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded transition-all"
                style={{ width: `${widthPercent}%` }}
              />
            </div>
            <span className="text-xs text-gray-700 w-10 text-right flex-shrink-0">
              {point.count}
            </span>
          </div>
        )
      })}
    </div>
  )
}

function AlertDeliverySummary({ stats }: { stats: AlertDeliveryStats }) {
  const total = stats.sent + stats.failed + stats.pending

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-2xl font-semibold text-success-600">{stats.sent}</p>
          <p className="text-sm text-gray-500">Sent</p>
        </div>
        <div>
          <p className="text-2xl font-semibold text-danger-600">{stats.failed}</p>
          <p className="text-sm text-gray-500">Failed</p>
        </div>
        <div>
          <p className="text-2xl font-semibold text-warning-600">{stats.pending}</p>
          <p className="text-sm text-gray-500">Pending</p>
        </div>
      </div>
      {total > 0 && (
        <div className="flex h-4 rounded-full overflow-hidden bg-gray-100">
          {stats.sent > 0 && (
            <div
              className="bg-success-500"
              style={{ width: `${(stats.sent / total) * 100}%` }}
            />
          )}
          {stats.failed > 0 && (
            <div
              className="bg-danger-500"
              style={{ width: `${(stats.failed / total) * 100}%` }}
            />
          )}
          {stats.pending > 0 && (
            <div
              className="bg-warning-500"
              style={{ width: `${(stats.pending / total) * 100}%` }}
            />
          )}
        </div>
      )}
      <div className="flex justify-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <Badge variant="success">Sent</Badge>
        </span>
        <span className="flex items-center gap-1">
          <Badge variant="danger">Failed</Badge>
        </span>
        <span className="flex items-center gap-1">
          <Badge variant="warning">Pending</Badge>
        </span>
      </div>
    </div>
  )
}

function AdminDashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 bg-gray-200 rounded w-64 animate-pulse" />
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="bg-white shadow rounded-lg p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
            <div className="h-8 bg-gray-200 rounded w-16" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {Array.from({ length: 2 }, (_, i) => (
          <div key={i} className="bg-white shadow rounded-lg p-6 animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-40 mb-4" />
            <div className="space-y-3">
              {Array.from({ length: 5 }, (_, j) => (
                <div key={j} className="h-6 bg-gray-100 rounded" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Icons
function OrgIcon() {
  return (
    <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  )
}

function TrialIcon() {
  return (
    <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function PaymentIcon() {
  return (
    <svg className="h-6 w-6 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function DetectionIcon() {
  return (
    <svg className="h-6 w-6 text-warning-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  )
}
